import { useCallback, useMemo, useState } from "react";
import { AttachFile, KeyboardArrowDown, MailOutline } from "@mui/icons-material";
import { toast } from "sonner";
import {
  ApplicantTaskProgress,
  ApplicantTaskStatus,
  DocumentationStep,
  ResourceBookingSchema,
} from "../../data/types";
import { useAddBookingTaskActivity, useCompleteSignIn, useLoadBookings } from "../../hooks/authhooks";
import UserAvatar from "../Mobile/UserAvatar";
import {
  canApplicantSetStatus,
  ensureStepsHaveDefaultTasks,
  findProgress,
  flattenStepTasks,
  formatRelativeSubmitted,
  hasAgencyContact,
  summarizeApplicantProgress,
  TASK_STATUS_OPTIONS,
  upsertProgressStatus,
} from "../../utils/documentationSteps";

type ApplicantDocumentationChecklistProps = {
  booking: ResourceBookingSchema
  steps?: DocumentationStep[]
  onChecklistChange?: (checklist: ApplicantTaskProgress[]) => void
};

/** Applicant-facing documentation steps/tasks with status and in-place activity. */
const ApplicantDocumentationChecklist = ({
  booking,
  steps = [],
  onChecklistChange,
}: ApplicantDocumentationChecklistProps) => {
  const { user } = useCompleteSignIn();
  const loggedinUid = user?.uid || "";
  const { updateBookingAsync } = useLoadBookings(loggedinUid);
  const { submitTaskActivity, isSubmittingTaskActivity } = useAddBookingTaskActivity(loggedinUid);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const isSaving = isSavingStatus || isSubmittingTaskActivity;

  const normalizedSteps = useMemo(() => ensureStepsHaveDefaultTasks(steps), [steps]);
  const tasks = useMemo(() => flattenStepTasks(normalizedSteps), [normalizedSteps]);
  const summary = useMemo(
    () => summarizeApplicantProgress(normalizedSteps, booking.applicant_checklist || []),
    [normalizedSteps, booking.applicant_checklist]
  );
  const percent = summary.total === 0 ? 0 : Math.round((summary.completed / summary.total) * 100);
  const isReadOnly = booking.status === "canceled" || booking.status === "completed";
  const authorName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.username ||
    user?.email ||
    "Applicant";

  const persistChecklist = useCallback(
    async (next: ApplicantTaskProgress[]) => {
      const communityId = booking.community_id || "";
      const bookingId = booking.id || "";
      if (!communityId || !bookingId) return false;
      const success = await updateBookingAsync(communityId, bookingId, { applicant_checklist: next });
      if (success) {
        onChecklistChange?.(next);
        return true;
      }
      toast.error("Unable to update task status.");
      return false;
    },
    [booking.community_id, booking.id, onChecklistChange, updateBookingAsync]
  );

  const handleStatusChange = async (stepId: string, taskId: string, status: ApplicantTaskStatus) => {
    const current = findProgress(booking.applicant_checklist, stepId, taskId)?.status || "not_started";
    if (isReadOnly || !canApplicantSetStatus(status, current)) return;
    setIsSavingStatus(true);
    await persistChecklist(upsertProgressStatus(booking.applicant_checklist, stepId, taskId, status));
    setIsSavingStatus(false);
  };

  const handleSubmitActivity = async (stepId: string, taskId: string) => {
    const communityId = booking.community_id || "";
    const bookingId = booking.id || "";
    if (!communityId || !bookingId || (!comment.trim() && !attachment)) return;
    try {
      const updated = await submitTaskActivity(communityId, bookingId, {
        stepId,
        taskId,
        message: comment.trim() || undefined,
        authorName,
        authorAvatar: user?.avatar,
        authorKind: "user",
        file: attachment || undefined,
      });
      if (updated?.applicant_checklist) {
        onChecklistChange?.(updated.applicant_checklist);
        setComment("");
        setAttachment(null);
        return;
      }
      toast.error("Unable to add comment.");
    } catch {
      toast.error("Unable to add comment.");
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="applicant-documentation-checklist">
      <div>
        <label className="block text-[#808080] text-[16px] font-raleway font-medium">Documentation checklist</label>
        {normalizedSteps.length === 0 ? (
          <p className="mt-2 font-open-sans text-[14px] text-[#4D4D4D]" data-testid="checklist-empty">
            Your partner has not added checklist steps yet.
          </p>
        ) : (
          <>
            <p className="mt-2 mb-1 font-raleway text-[13px]" data-testid="checklist-progress">
              {summary.completed} of {summary.total} tasks done
            </p>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#E9E9E9]">
              <div className="h-full bg-[#22C55E]" style={{ width: `${percent}%` }} />
            </div>
          </>
        )}
      </div>

      {tasks.map((task, index) => {
        const progress = findProgress(booking.applicant_checklist, task.step_id, task.id);
        const status = (progress?.status || "not_started") as ApplicantTaskStatus;
        const isExpanded = expandedTaskId === task.id;
        const activity = progress?.activity || [];
        return (
          <div
            key={`${task.step_id}-${task.id}`}
            className="rounded-xl border border-[#E9E9E9] bg-white p-4"
            data-testid={`applicant-task-${task.id}`}
          >
            <div className="mb-2 flex items-start gap-2">
              <span className="rounded-full bg-[#F3F4F6] px-3 py-1 font-raleway text-[12px]">Task {index + 1}</span>
              <label className="ml-auto">
                <span className="sr-only">Status for {task.title || "task"}</span>
                <select
                  className="rounded-full border border-[#E9E9E9] px-3 py-1 font-raleway text-[13px] disabled:bg-[#F8F8F8]"
                  value={status}
                  aria-label={`Status for ${task.title || "task"}`}
                  disabled={isReadOnly || isSaving}
                  onChange={(event) =>
                    handleStatusChange(task.step_id, task.id, event.target.value as ApplicantTaskStatus)
                  }
                >
                  {TASK_STATUS_OPTIONS.map((option) => (
                    <option
                      key={option.id}
                      value={option.id}
                      disabled={!canApplicantSetStatus(option.id, status)}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {task.step_title ? (
              <p className="font-raleway text-[12px] text-[#808080]">{task.step_title}</p>
            ) : null}
            <h3 className="font-raleway text-[16px] font-semibold">{task.title || "Untitled task"}</h3>
            {task.description ? (
              <p className="mt-1 font-open-sans text-[14px] text-[#4D4D4D]">{task.description}</p>
            ) : null}
            <div className="mt-3 flex flex-wrap gap-2">
              {hasAgencyContact(task.agency) && task.agency?.name ? (
                <span className="rounded-full bg-[#EFF8FA] px-3 py-1 font-raleway text-[12px] text-primary-200">
                  {task.agency.name}
                </span>
              ) : null}
              {task.agency?.email ? (
                <a
                  className="inline-flex items-center rounded-full bg-[#EFF8FA] px-3 py-1 font-raleway text-[12px] text-primary-200"
                  href={`mailto:${task.agency.email}`}
                >
                  <MailOutline className="mr-1" style={{ height: 14, width: 14 }} />
                  {task.agency.email}
                </a>
              ) : null}
            </div>
            <button
              type="button"
              className="mt-3 flex items-center font-raleway text-[13px] text-primary-200"
              onClick={() => {
                setExpandedTaskId(isExpanded ? null : task.id);
                setComment("");
                setAttachment(null);
              }}
              data-testid={`toggle-activity-${task.id}`}
            >
              Activity {activity.length > 0 ? `(${activity.length})` : ""}
              <KeyboardArrowDown className={`ml-1 ${isExpanded ? "rotate-180" : ""}`} style={{ height: 18, width: 18 }} />
            </button>
            {isExpanded ? (
              <div className="mt-3 border-t border-[#E9E9E9] pt-3" data-testid={`task-activity-${task.id}`}>
                {!isReadOnly ? (
                  <div className="mb-3 rounded-xl border border-[#E9E9E9] bg-white p-3">
                    <textarea
                      className="min-h-[72px] w-full resize-none font-open-sans text-[14px] outline-none"
                      placeholder="Add a comment..."
                      aria-label="Add a comment"
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                    />
                    <div className="mt-2 flex items-center">
                      <label className="cursor-pointer text-[#808080]">
                        <AttachFile />
                        <input
                          type="file"
                          className="hidden"
                          aria-label="Attach file"
                          onChange={(event) => setAttachment(event.target.files?.[0] || null)}
                        />
                      </label>
                      {attachment ? (
                        <span className="ml-2 font-open-sans text-[12px] text-[#4D4D4D]">{attachment.name}</span>
                      ) : null}
                      <button
                        type="button"
                        className="ml-auto rounded-md bg-primary-200 px-3 py-1.5 font-raleway text-[14px] text-white disabled:bg-[#E9E9E9] disabled:text-[#808080]"
                        disabled={isSaving || (!comment.trim() && !attachment)}
                        onClick={() => handleSubmitActivity(task.step_id, task.id)}
                      >
                        Comment
                      </button>
                    </div>
                  </div>
                ) : null}
                {activity.length === 0 ? (
                  <p className="py-4 text-center font-open-sans text-[13px] text-[#808080]">No activity yet for this task.</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {activity.map((item) => (
                      <div key={item.id} className="rounded-xl bg-[#F8FAFB] p-3">
                        <div className="mb-2 flex items-center gap-2">
                          <UserAvatar firstName={item.author_name} avatar={item.author_avatar} size="sm" />
                          <p className="font-raleway text-[14px] font-semibold">{item.author_name || "Applicant"}</p>
                          <p className="font-open-sans text-[12px] text-[#808080]">
                            {item.created ? formatRelativeSubmitted(item.created) : ""}
                          </p>
                        </div>
                        {item.message ? <p className="font-open-sans text-[14px] text-[#333333]">{item.message}</p> : null}
                        {(item.attachments || []).map((file) => (
                          <div key={file.file_url || file.file_name} className="mt-2 flex items-center rounded-full bg-white px-3 py-2">
                            <AttachFile className="mr-2 text-primary-400" style={{ height: 16, width: 16 }} />
                            <span className="font-open-sans text-[13px]">{file.file_name}</span>
                            {file.file_url ? (
                              <a className="ml-auto font-raleway text-[13px] text-primary-400" href={file.file_url} target="_blank" rel="noreferrer">
                                View
                              </a>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default ApplicantDocumentationChecklist;
